<!-- Ask for a change, in words. THIS FILE IS YOURS.

     "Add more drawers by the cooktop." "Make the left wall 3.2 metres." "Swap the doors for
     drawers." The model reads the kitchen through a tool, decides what to do, and does it
     through the same operations a finger uses -- so it cannot produce a kitchen the maker
     will not build, and every refusal the app already makes comes back to it as a reason.

     The loop is split. The server runs one model turn and hands back the tool calls; this
     component runs them against the live scene and posts the results for the next turn.
     One Undo entry per question, taken before the first tool runs. -->
<template>
  <div class="ask">
    <div class="thread" ref="threadEl">
      <p v-if="!thread.length" class="intro">
        Say what you want changed. Ideas:
      </p>
      <div v-if="!thread.length" class="chips">
        <button v-for="c in CHIPS" :key="c" type="button" @click="send(c)" :disabled="busy">{{ c }}</button>
      </div>
      <div v-for="(m, i) in thread" :key="i" class="msg" :class="m.who">
        <p v-if="m.text">{{ m.text }}</p>
        <ul v-if="m.did?.length" class="did">
          <li v-for="(d, j) in m.did" :key="j" :class="{ err: d.err }">{{ d.text }}</li>
        </ul>
      </div>
      <p v-if="busy" class="msg them thinking">Working on it…</p>
      <p v-if="error" class="msg err">{{ error }}</p>
    </div>
    <form class="compose" @submit.prevent="send(draft)">
      <input v-model.trim="draft" type="text" enterkeyhint="send" :disabled="busy"
             placeholder="Add a pantry beside the fridge…" aria-label="What would you like changed?" />
      <button type="submit" :disabled="busy || !draft" aria-label="Send">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10h13"/><path d="M11 5l5 5-5 5"/></svg>
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import { describe, range } from '../kitchen/tools.js';
import { review } from '../kitchen/guidelines.js';
import { schedule } from '../kitchen/schedule.js';

const props = defineProps({
  plan: Object, maker: Object, kitchen: Object,
  /** The page's own operations, so a tool does exactly what a tap would. */
  ops: Object,
});

const CHIPS = [
  'Add more drawers by the cooktop',
  'Make the back wall 4.5 metres',
  'Swap the cupboards for drawers',
  'Put a pantry beside the fridge',
];

const thread = ref([]);
const messages = [];
const draft = ref('');
const busy = ref(false);
const error = ref(null);
const threadEl = ref(null);

async function send(text) {
  if (!text || busy.value) return;
  draft.value = '';
  error.value = null;
  thread.value.push({ who: 'me', text });
  messages.push({ role: 'user', content: text });
  busy.value = true;
  let remembered = false;
  const did = [];
  try {
    for (let turn = 0; turn < 12; turn++) {
      const res = await fetch('/api/chat', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error ?? `The assistant is not answering (${res.status}).`);
      }
      const { content, stop_reason } = await res.json();
      messages.push({ role: 'assistant', content });
      const calls = content.filter((b) => b.type === 'tool_use');
      const text = content.filter((b) => b.type === 'text').map((b) => b.text).join('\n').trim();
      if (stop_reason !== 'tool_use' || !calls.length) {
        thread.value.push({ who: 'them', text: text || 'Done.', did: did.splice(0) });
        break;
      }
      if (!remembered) { props.ops.remember(); remembered = true; }
      const results = [];
      for (const call of calls) {
        const r = execute(call.name, call.input);
        if (r.said) did.push({ text: r.said, err: !!r.error });
        results.push({
          type: 'tool_result', tool_use_id: call.id,
          content: JSON.stringify(r.error ? { error: r.error } : r.result ?? { ok: true }),
          ...(r.error ? { is_error: true } : {}),
        });
      }
      messages.push({ role: 'user', content: results });
      props.ops.changed();
    }
  } catch (e) {
    error.value = String(e?.message ?? e);
  } finally {
    busy.value = false;
    await nextTick();
    threadEl.value?.scrollTo({ top: threadEl.value.scrollHeight, behavior: 'smooth' });
  }
}

/**
 * A tool call, carried out.
 *
 * Every branch is one of the page's own operations, so the assistant can do exactly what
 * a finger can and nothing a finger cannot. Refusals come back as errors, in the maker's
 * words, for the model to explain. `said` is the one line shown under the reply.
 */
function execute(name, input) {
  const { plan, maker, ops } = props;
  const spec = (id) => maker.cabinets.find((c) => c.id === id);
  const find = (itemId) => {
    for (const run of plan.runs) { const it = run.items.find((i) => i.id === itemId); if (it) return { run, it }; }
    return null;
  };
  try {
    switch (name) {
      case 'describe_kitchen':
        return { result: describe(plan, maker, review(plan, maker), schedule(plan, maker)) };
      case 'list_range':
        return { result: range(maker, input.kind ?? 'any') };
      case 'add_cabinet': {
        const sp = spec(input.specId);
        if (!sp) return { error: `no cabinet ${input.specId} in the range` };
        const r = ops.add(input.specId, input.wallId || null, input.near || 'anywhere');
        return r.ok ? { said: `Added a ${sp.name}`, result: { ok: true, itemId: r.itemId } } : { error: r.why, said: r.why };
      }
      case 'remove_cabinet': {
        const f = find(input.itemId);
        if (!f) return { error: 'no such cabinet' };
        const r = ops.remove(f.run, f.it);
        return r.ok ? { said: `Took out the ${spec(f.it.specId)?.name ?? 'cabinet'}` } : { error: r.why, said: r.why };
      }
      case 'swap_cabinet': {
        const f = find(input.itemId);
        const sp = spec(input.specId);
        if (!f || !sp) return { error: 'no such cabinet' };
        if (sp.kind !== f.run.kind && !(sp.kind === 'tall' && f.run.kind === 'base')) return { error: `a ${sp.kind} cabinet cannot go on a ${f.run.kind} run` };
        const r = ops.swap(f.run, f.it, input.specId);
        return r.ok ? { said: `Changed it to a ${sp.name}` } : { error: r.why, said: r.why };
      }
      case 'set_width': {
        const f = find(input.itemId);
        if (!f) return { error: 'no such cabinet' };
        const sp = spec(f.it.specId);
        if (sp && (input.mm < sp.stretch[0] || input.mm > sp.stretch[1])) {
          return { error: `${sp.name} is cut between ${sp.stretch[0]} and ${sp.stretch[1]}mm` };
        }
        const r = ops.setWidth(f.run, f.it, input.mm);
        return r.ok ? { said: `Made the ${sp?.name ?? 'cabinet'} ${input.mm}mm` } : { error: r.why, said: r.why };
      }
      case 'move_cabinet': {
        const f = find(input.itemId);
        if (!f) return { error: 'no such cabinet' };
        const r = ops.move(f.run, f.it, input.wallId, input.index);
        return r.ok ? { said: `Moved the ${spec(f.it.specId)?.name ?? 'cabinet'}` } : { error: r.why, said: r.why };
      }
      case 'set_wall_length': {
        const r = ops.setWall(input.wallId, input.mm);
        return r.ok ? { said: `${input.wallId} wall is now ${input.mm}mm` } : { error: r.why, said: r.why };
      }
      case 'set_shape':
        ops.setShape(input.shape);
        return { said: `Shape: ${input.shape}` };
      case 'toggle_wall':
        ops.toggleWall(input.wallId);
        return { said: `Cabinets ${plan.runs.some((r) => r.wallId === input.wallId) ? 'on' : 'off'} the ${input.wallId} wall` };
      case 'set_finish': {
        const list = { doorStyleId: 'doorStyles', colourId: 'colours', benchtopId: 'benchtops', splashbackId: 'splashbacks', handleId: 'handles' }[input.field];
        const opt = maker[list]?.find((o) => o.id === input.optionId);
        if (!opt) return { error: `no ${input.field} called ${input.optionId}` };
        ops.pick(input.field, input.optionId);
        return { said: `${opt.name}` };
      }
      case 'lock': {
        const f = find(input.itemId);
        if (!f) return { error: 'no such cabinet' };
        ops.lock(f.it, input.locked);
        return { said: `${input.locked ? 'Kept' : 'Freed'} the ${spec(f.it.specId)?.name ?? 'cabinet'}` };
      }
      case 'design_options': {
        const d = ops.designOptions();
        return { result: d.options.map((o) => ({ optionId: o.id, name: o.name, why: o.why })), said: `${d.options.length} kitchens drawn up` };
      }
      case 'choose_option': {
        const r = ops.chooseOption(input.optionId);
        return r.ok ? { said: `Applied ${r.name}` } : { error: r.why };
      }
      default:
        return { error: `no tool called ${name}` };
    }
  } catch (e) {
    return { error: String(e?.message ?? e) };
  }
}
</script>

<style scoped>
.ask { display: flex; flex-direction: column; min-height: 220px; max-height: 44vh; }
.thread { flex: 1; overflow-y: auto; padding: 0 16px 8px; display: grid; gap: 8px; align-content: start; }
.intro { margin: 2px 0 0; font: 400 13px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim); }
.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chips button {
  border: 1px solid var(--k-line); border-radius: 999px; background: var(--k-paper); cursor: pointer;
  padding: 8px 12px; min-height: 36px; font: 500 12px/1 var(--ion-font-family, system-ui); color: var(--k-dim);
}
.msg { margin: 0; max-width: 88%; padding: 9px 12px; border-radius: 13px; font: 400 13.5px/1.45 var(--ion-font-family, system-ui); }
.msg p { margin: 0; }
.msg.me { align-self: end; justify-self: end; background: var(--k-ink); color: var(--k-paper); border-bottom-right-radius: 4px; }
.msg.them { justify-self: start; background: var(--k-fill); color: var(--k-ink); border-bottom-left-radius: 4px; }
.msg.thinking { color: var(--k-mute); }
.msg.err { justify-self: start; background: color-mix(in oklab, var(--k-warn) 9%, var(--k-paper)); color: var(--k-warn); }
.did { list-style: none; margin: 6px 0 0; padding: 0; display: grid; gap: 2px; }
.did li { font: 400 11.5px/1.35 var(--ion-font-family, system-ui); color: var(--k-mute); padding-left: 10px; position: relative; }
.did li::before { content: ''; position: absolute; left: 0; top: 6px; width: 4px; height: 4px; border-radius: 999px; background: var(--k-accent); }
.did li.err { color: var(--k-warn); }
.compose { display: flex; gap: 8px; padding: 8px 16px 0; border-top: 1px solid var(--k-line); }
.compose input {
  flex: 1; min-width: 0; border: 1px solid var(--k-line); border-radius: 12px; background: var(--k-paper);
  padding: 11px 13px; min-height: 46px; font: 400 15px/1 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.compose input:focus { outline: 2px solid var(--k-ink); outline-offset: 1px; }
.compose button {
  flex: 0 0 46px; width: 46px; height: 46px; border: 0; border-radius: 12px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper); display: grid; place-items: center;
}
.compose button:disabled { opacity: .35; }
</style>
